import React, { useState, useRef } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock, GraphContainer, ResultMessage } from '../SimulationTemplate';
import { Text } from '../../../components/ui/typography/Text';
import { OutlineButton } from '../../../components/ui/buttons/OutlineButton';
import { AccentButton } from '../../../components/ui/buttons/AccentButton';
import { Input } from '../../../components/ui/Input';
import { Brain, Microscope, BookOpen } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';

// Custom tooltip component that doesn't show on mobile
const CustomTooltip = ({ active, payload, label }: any) => {
  // Only show tooltip on larger screens
  if (typeof window !== 'undefined' && window.innerWidth < 640 || !active || !payload || payload.length === 0) return null;
  
  return (
    <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm text-sm">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((entry: any, index: number) => (
        <p key={`item-${index}`} style={{ color: entry.color }}>
          {entry.name}: {entry.value}%
        </p>
      ))}
    </div>
  );
};

const FalseConsensusSimulation: React.FC = () => {
  const [stage, setStage] = useState<'intro' | 'choice' | 'estimate' | 'results'>('intro');
  const [userChoice, setUserChoice] = useState<string | null>(null);
  const [userEstimate, setUserEstimate] = useState('');
  const [estimateSubmitted, setEstimateSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  const simulationRef = useRef<HTMLDivElement>(null);

  const actualResults = {
    agreedPercentage: 25,
    disagreedPercentage: 75
  };

  // Original study data for chart
  const getOriginalStudyData = () => {
    return [
      {
        name: "Yes-Sayers",
        "Would Wear Sign": 62,
        "Would Not Wear Sign": 38,
      },
      {
        name: "No-Sayers",
        "Would Wear Sign": 15,
        "Would Not Wear Sign": 85,
      },
      {
        name: "Actual Results",
        "Would Wear Sign": 25,
        "Would Not Wear Sign": 75,
      }
    ];
  };

  const handleChoice = (choice: string) => {
    setUserChoice(choice);
    setStage('estimate');
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleEstimateChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setError('');
    const inputValue = e.target.value.replace(/\D/g, '');
    if (inputValue === '' || (parseInt(inputValue, 10) <= 100)) {
      setUserEstimate(inputValue);
    }
  };

  const handleEstimateSubmit = () => {
    if (userEstimate === '') {
      setError('Please enter a number between 0 and 100');
      return;
    }
    
    const estimate = parseInt(userEstimate, 10);
    if (isNaN(estimate)) {
      setError('Please enter a valid number');
      return;
    }
    
    if (estimate < 0 || estimate > 100) {
      setError('Please enter a number between 0 and 100');
      return;
    }

    setError('');
    setEstimateSubmitted(true);
    setStage('results');
  };

  const handleReset = () => {
    setStage('choice');
    setUserChoice(null);
    setUserEstimate('');
    setEstimateSubmitted(false);
    setError('');
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper function to prepare chart data
  const getChartData = () => {
    if (!userChoice || !userEstimate) return [];
    
    const userChoiceLabel = userChoice === 'yes' ? 'Would wear sign' : 'Would not wear sign';
    const oppositeChoiceLabel = userChoice === 'yes' ? 'Would not wear sign' : 'Would wear sign';
    
    return [
      {
        name: 'Expectation',
        [userChoiceLabel]: parseInt(userEstimate),
        [oppositeChoiceLabel]: 100 - parseInt(userEstimate),
      },
      {
        name: 'Reality',
        [userChoiceLabel]: userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage,
        [oppositeChoiceLabel]: userChoice === 'yes' ? actualResults.disagreedPercentage : actualResults.agreedPercentage,
      },
    ];
  };

  const simulationContent = (
    <div ref={simulationRef} className="space-y-8">
      {/* Choice Selection */}
      <SimulationStep
        title="Make Your Choice"
        isActive={stage === 'choice'}
      >
        <div className="space-y-6">
          <Text className="text-gray-700">
            Imagine you're walking down a busy street and you see someone wearing a sign that says 'Repent!' Would you be willing to wear a similar sign for 30 minutes if you were paid $20?
          </Text>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <OutlineButton 
              onClick={() => handleChoice('yes')}
              disabled={stage !== 'choice'}
              active={userChoice === 'yes'}
            >
              Yes, I would
            </OutlineButton>
            <OutlineButton 
              onClick={() => handleChoice('no')}
              disabled={stage !== 'choice'}
              active={userChoice === 'no'}
            >
              No, I would not
            </OutlineButton>
          </div>
          {userChoice && stage !== 'choice' && (
            <Text className="text-gray-600 italic mt-4">
              You selected: {userChoice === 'yes' ? 'Yes, I would' : 'No, I would not'}
            </Text>
          )}
        </div>
      </SimulationStep>

      {/* Estimate Input */}
      {stage !== 'choice' && (
        <SimulationStep
          title="Estimate Others' Choices"
          isActive={stage === 'estimate'}
        >
          <div className="space-y-6">
            <Text className="text-gray-700">
              What percentage of other people do you think would make the <strong>same choice</strong> as you?
            </Text>
            <div className="max-w-[280px] mx-auto">
              <div className="relative flex items-center">
                <Input
                  type="text"
                  value={userEstimate}
                  onChange={handleEstimateChange}
                  className="text-center text-xl h-12"
                  disabled={estimateSubmitted}
                  placeholder="0-100"
                />
                <Text className="absolute right-4 text-gray-500 pointer-events-none">%</Text>
              </div>
              {error && (
                <Text className="text-red-500 mt-2 text-center">
                  {error}
                </Text>
              )}
            </div>
            {Object.keys(userEstimate).length > 0 && stage === 'estimate' && (
              <div className="flex justify-center mt-6">
                <AccentButton onClick={handleEstimateSubmit}>
                  Submit Estimate
                </AccentButton>
              </div>
            )}
          </div>
        </SimulationStep>
      )}

      {/* Results */}
      {stage === 'results' && (
        <div className="space-y-6">
          <ResultBlock 
            title="Understanding the Results"
            icon={Brain}
            variant="highlight"
          >
            <div className="space-y-8">
              {/* Your Results */}
              <div className="space-y-5">
                <Text className="text-gray-800 font-medium text-lg">
                  Your Results
                </Text>

                {/* Feedback on user's estimate - moved to top */}
                {userEstimate && (
                  <ResultMessage 
                    isCorrect={Math.abs(parseInt(userEstimate) - (userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage)) <= 15}
                    message={Math.abs(parseInt(userEstimate) - (userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage)) <= 15
                      ? `Your estimate was quite accurate! You were only ${Math.abs(parseInt(userEstimate) - (userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage))} percentage points ${parseInt(userEstimate) > (userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage) ? 'above' : 'below'} the actual result.`
                      : parseInt(userEstimate) > (userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage)
                        ? `Your estimate was ${Math.abs(parseInt(userEstimate) - (userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage))} percentage points higher than the actual result, showing how the false consensus effect can influence our perceptions.`
                        : `Your estimate was ${Math.abs(parseInt(userEstimate) - (userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage))} percentage points lower than the actual result. Interestingly, you underestimated how many others would share your view, which is contrary to the false consensus effect.`
                    }
                  />
                )}

                <Text className="text-gray-700">
                  You were asked if you would wear a "Repent!" sign for 30 minutes in exchange for $20. You indicated that you <span className="font-medium">{userChoice === 'yes' ? 'would' : 'would not'}</span> do this.
                </Text>
                
                <Text className="text-gray-700">
                  Based on your choice, you estimated that <span className="text-blue-600 font-medium">{userEstimate}%</span> of other people would make the same decision as you. In the original study, <span className="text-emerald-600 font-medium">{userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage}%</span> of people made the same choice as you.
                </Text>

                <GraphContainer>
                  <div className="flex flex-col h-full w-full overflow-x-auto">
                    <div className="h-[350px] md:h-[300px] min-w-[400px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={getChartData()}
                          layout="vertical"
                          margin={{ top: 20, right: 30, left: 25, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis type="number" domain={[0, 100]} />
                          <YAxis dataKey="name" type="category" width={80} tickMargin={10} />
                          <Tooltip content={<CustomTooltip />} />
                          
                          {userChoice && (
                            <>
                              <Bar 
                                dataKey={userChoice === 'yes' ? 'Would wear sign' : 'Would not wear sign'} 
                                stackId="a"
                                fill="#3b82f6"
                                name="Same as your choice"
                              >
                                <LabelList 
                                  dataKey={userChoice === 'yes' ? 'Would wear sign' : 'Would not wear sign'} 
                                  position="center" 
                                  fill="#ffffff" 
                                  formatter={(value: number) => `${value}%`}
                                />
                              </Bar>
                              <Bar 
                                dataKey={userChoice === 'yes' ? 'Would not wear sign' : 'Would wear sign'} 
                                stackId="a" 
                                fill="#9ca3af"
                                name="Opposite of your choice"
                              >
                                <LabelList 
                                  dataKey={userChoice === 'yes' ? 'Would not wear sign' : 'Would wear sign'} 
                                  position="center" 
                                  fill="#ffffff" 
                                  formatter={(value: number) => `${value}%`}
                                />
                              </Bar>
                            </>
                          )}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </GraphContainer>

                <div className="flex items-center justify-center gap-8 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded"></div>
                    <span>Same as your choice</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-400 rounded"></div>
                    <span>Opposite choice</span>
                  </div>
                </div>

              </div>

              {/* How This Relates to False Consensus Effect */}
              <div className="space-y-4">
                <Text className="text-gray-800 font-medium text-lg">
                  How This Relates to False Consensus Bias
                </Text>
                <Text className="text-gray-700">
                  {parseInt(userEstimate) > (userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage)
                    ? <>
                        Your estimate was <span className="text-blue-600 font-medium">{Math.abs(parseInt(userEstimate) - (userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage))} percentage points higher</span> than the actual result. This demonstrates the false consensus effect in action — a cognitive bias where we tend to overestimate how many other people share our preferences and choices. We often assume our own decisions are more "normal" or common than they actually are.
                      </>
                    : <>
                        Your estimate was <span className="text-blue-600 font-medium">{Math.abs(parseInt(userEstimate) - (userChoice === 'yes' ? actualResults.agreedPercentage : actualResults.disagreedPercentage))} percentage points lower</span> than the actual result. This is an interesting deviation from the typical false consensus effect, where people usually overestimate how many others share their views. Your more conservative estimate suggests you may have been actively considering that others might think differently than you do.
                      </>
                  }
                </Text>
                <Text className="text-gray-700">
                  This comparison between your prediction and the actual data highlights an important aspect of human psychology: our own choices and preferences can significantly influence how we perceive others' behavior. Being aware of this tendency can help us make more objective assessments in various life situations.
                </Text>
              </div>

              {/* What the Original Study Found */}
              <div className="space-y-4">
                <Text className="text-gray-800 font-medium text-lg">
                  What the Original Study Found
                </Text>
                <Text className="text-gray-700">
                  The 1977 research by Ross, Greene, and House at Stanford University explored how our own choices affect our predictions about others' behavior. Their study presented participants with various scenarios, including the "Repent" sign situation, and found a consistent pattern of false consensus across different decisions.
                </Text>
                <div className="pl-4 border-l-2 border-blue-100 space-y-3">
                  <Text className="text-gray-700">
                    • Participants who agreed to wear the sign predicted that <span className="text-blue-600 font-medium">62%</span> of others would also agree — showing a strong bias toward their own choice
                  </Text>
                  <Text className="text-gray-700">
                    • Those who refused believed that only <span className="text-blue-600 font-medium">15%</span> would agree — significantly underestimating others' willingness
                  </Text>
                  <Text className="text-gray-700">
                    • The actual data showed that <span className="text-emerald-600 font-medium">25%</span> of participants agreed to wear the sign, while <span className="text-emerald-600 font-medium">75%</span> refused, demonstrating how both groups' predictions were skewed by their own decisions
                  </Text>
                </div>
                
                {/* Original study results visualization */}
                <Text className="text-gray-800 font-medium text-center mb-2">
                  Study Participants' Predictions vs. Reality
                </Text>
                <GraphContainer>
                  <div className="flex flex-col h-full w-full overflow-x-auto">
                    <div className="h-[350px] md:h-[300px] min-w-[500px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={getOriginalStudyData()}
                          layout="vertical"
                          margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis type="number" domain={[0, 100]} />
                          <YAxis dataKey="name" type="category" width={90} tickMargin={5} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="Would Wear Sign" fill="#3b82f6" stackId="a" name="Would wear sign">
                            <LabelList dataKey="Would Wear Sign" position="center" fill="#ffffff" formatter={(value: number) => `${value}%`} />
                          </Bar>
                          <Bar dataKey="Would Not Wear Sign" fill="#9ca3af" stackId="a" name="Would not wear sign">
                            <LabelList dataKey="Would Not Wear Sign" position="center" fill="#ffffff" formatter={(value: number) => `${value}%`} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </GraphContainer>
                
                <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-500 rounded"></div>
                    <span>Would wear sign</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-400 rounded"></div>
                    <span>Would not wear sign</span>
                  </div>
                </div>
                
                <Text className="text-gray-600 text-sm text-center italic">
                  Notice how each group overestimated the number of people who would make the same choice as them.
                </Text>
                
                <Text className="text-gray-700">
                  This study revealed a fundamental aspect of human psychology: we tend to use our own choices as a reference point for predicting others' behavior. Both groups showed strong bias, with each side overestimating the number of people who would make the same choice they did. This pattern has been consistently replicated across various scenarios and cultural contexts.
                </Text>
              </div>
            </div>
          </ResultBlock>

          <ResultBlock 
            title="About the Study"
            icon={BookOpen}
            variant="muted"
          >
            <div className="space-y-4">
              <Text className="text-gray-700">
                Based on research by Ross, Greene, & House (1977), "The False Consensus Effect: An Egocentric Bias in Social Perception and Attribution Processes," published in the Journal of Experimental Social Psychology. The study revealed how people tend to overestimate the extent to which their own behaviors and beliefs are shared by others.
              </Text>
              <Text className="text-sm text-gray-500 italic">
                This simulation is designed for educational purposes. Individual results may vary.
              </Text>
            </div>
          </ResultBlock>

          <div className="flex justify-center mt-6">
            <AccentButton onClick={handleReset}>
              Try Again
            </AccentButton>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <SimulationTemplate
      title="The False Consensus Effect"
      description="In this simulation, you'll explore how we tend to overestimate the commonality of our own choices and beliefs. Based on a classic 1977 study by Ross, Greene, and House at Stanford University."
    >
      {stage === 'intro' ? (
        <div className="flex justify-center">
          <AccentButton onClick={() => setStage('choice')}>
            Start Simulation
          </AccentButton>
        </div>
      ) : (
        simulationContent
      )}
    </SimulationTemplate>
  );
};

export default FalseConsensusSimulation; 